import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu, Phone, X } from 'lucide-react';
import { SITE } from '../data/site';
import { SERVICES } from '../data/services';
import { LOCATIONS } from '../data/locations';

const Dropdown = ({ label, href, items, testId, onDark }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        to={href}
        data-testid={testId}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium tracking-wide transition-colors ${onDark ? 'text-paper/80 hover:text-paper' : 'text-ink/80 hover:text-ink'}`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 w-[520px] -translate-x-1/2 pt-3"
          >
            <div className="grid grid-cols-2 gap-0 border border-line bg-paper shadow-[0_24px_60px_-20px_rgba(22,22,21,0.35)]">
              {items.map((it) => (
                <Link
                  key={it.href}
                  to={it.href}
                  data-testid={it.testId}
                  className="group border-b border-r border-line/70 px-5 py-4 transition-colors hover:bg-paper2"
                >
                  <span className="block font-serif text-lg text-ink transition-colors group-hover:text-bronze">{it.title}</span>
                  {it.sub && <span className="mt-0.5 block text-xs text-muted2">{it.sub}</span>}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const serviceItems = SERVICES.map((s) => ({ href: `/services/${s.slug}/`, title: s.navTitle, sub: s.category, testId: `nav-service-${s.slug}` }));
  const locationItems = LOCATIONS.map((l) => ({ href: `/service-areas/${l.slug}/`, title: l.city, sub: l.region, testId: `nav-area-${l.slug}` }));
  const onDark = !scrolled && location.pathname === '/';
  const linkCls = onDark ? 'text-paper/80 hover:text-paper' : 'text-ink/80 hover:text-ink';

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? 'border-b border-line/80 bg-paper/85 backdrop-blur-xl' : 'border-b border-transparent bg-transparent'
        }`}
      >
        <div className="container-site">
          <div className={`flex items-center justify-between transition-[height] duration-500 ${scrolled ? 'h-16' : 'h-20 md:h-24'}`}>
            <Link to="/" data-testid="header-logo" className="flex items-center" aria-label="Nebraska Backyard Living home">
              <img
                src={onDark ? '/images/logo-light.png' : '/images/logo.png'}
                alt="Nebraska Backyard Living"
                className={`w-auto transition-[height] duration-500 ${scrolled ? 'h-9 md:h-10' : 'h-11 md:h-12'}`}
              />
            </Link>

            <nav className="hidden items-center lg:flex" aria-label="Primary">
              <Dropdown label="Services" href="/services/" items={serviceItems} testId="nav-services" onDark={onDark} />
              <Dropdown label="Service Areas" href="/service-areas/" items={locationItems} testId="nav-service-areas" onDark={onDark} />
              <NavLink to="/projects/" data-testid="nav-projects" className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors ${linkCls}`}>Projects</NavLink>
              <NavLink to="/about/" data-testid="nav-about" className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors ${linkCls}`}>About</NavLink>
              <NavLink to="/blog/" data-testid="nav-blog" className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors ${linkCls}`}>Blog</NavLink>
              <NavLink to="/contact/" data-testid="nav-contact" className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors ${linkCls}`}>Contact</NavLink>
            </nav>

            <div className="flex items-center gap-2 md:gap-4">
              <a href={SITE.phoneHref} data-testid="header-phone" className={`hidden items-center gap-2 text-sm font-semibold transition-colors md:flex ${onDark ? 'text-paper hover:text-bronzelight' : 'text-ink hover:text-bronze'}`}>
                <Phone className="h-4 w-4" /> {SITE.phone}
              </a>
              <a href={SITE.phoneHref} data-testid="header-phone-mobile" className={`flex h-11 w-11 items-center justify-center md:hidden ${onDark ? 'text-paper' : 'text-ink'}`} aria-label={`Call ${SITE.phone}`}>
                <Phone className="h-5 w-5" />
              </a>
              <Link to="/get-a-quote/" data-testid="header-quote-cta" className="btn-primary hidden !px-5 !py-3 lg:inline-flex">Get a Free Quote</Link>
              <button
                data-testid="mobile-menu-button"
                onClick={() => setMobileOpen(true)}
                className={`flex h-11 w-11 items-center justify-center lg:hidden ${onDark ? 'text-paper' : 'text-ink'}`}
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-graphite text-paper lg:hidden"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <img src="/images/logo-light.png" alt="Nebraska Backyard Living" className="h-10 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="flex h-11 w-11 items-center justify-center" aria-label="Close menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 pb-8" aria-label="Mobile">
              {[
                { key: 'services', label: 'Services', items: serviceItems },
                { key: 'areas', label: 'Service Areas', items: locationItems },
              ].map((sec) => (
                <div key={sec.key} className="border-b border-paper/10">
                  <button
                    data-testid={`mobile-${sec.key}-toggle`}
                    onClick={() => setMobileSection(mobileSection === sec.key ? null : sec.key)}
                    className="flex w-full items-center justify-between py-5 font-serif text-2xl"
                    aria-expanded={mobileSection === sec.key}
                  >
                    {sec.label}
                    <ChevronDown className={`h-5 w-5 transition-transform ${mobileSection === sec.key ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileSection === sec.key && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="grid grid-cols-2 gap-x-4 pb-5">
                          {sec.items.map((it) => (
                            <Link key={it.href} to={it.href} data-testid={`mobile-${it.testId}`} className="py-2.5 text-sm text-paper/70">
                              {it.title}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              {[['Projects', '/projects/'], ['About', '/about/'], ['Blog', '/blog/'], ['Contact', '/contact/']].map(([label, href]) => (
                <Link key={href} to={href} data-testid={`mobile-nav-${label.toLowerCase()}`} className="block border-b border-paper/10 py-5 font-serif text-2xl">
                  {label}
                </Link>
              ))}
              <a href={SITE.phoneHref} data-testid="mobile-call-link" className="mt-8 flex items-center gap-3 text-lg font-semibold text-bronzelight">
                <Phone className="h-5 w-5" /> {SITE.phone}
              </a>
              <Link to="/get-a-quote/" data-testid="mobile-quote-cta" className="btn-primary mt-6 w-full">Get a Free Quote</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
